import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogHeader,
  DialogSelectTriggerContent,
  DialogTitle,
  FileTypeIcon,
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuSearch,
  Tooltip,
} from 'components/ui';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import {
  FIRM_DOCUMENTS_DOCUMENT_ORDERINGS,
  FIRM_DOCUMENTS_LABELS,
} from 'pages/Documents/FirmDocuments/FirmDocuments.constants';
import { FIRM_DOCUMENTS_TEST_IDS } from 'pages/Documents/FirmDocuments/FirmDocuments.testIds';
import {
  FirmDocumentsFilterDialogField,
  FirmDocumentsFilterDialogFieldLabel,
  FirmDocumentsFilterDialogFileTypeOptionContent,
  FirmDocumentsFilterDialogFileTypeOptionIcon,
  FirmDocumentsFilterDialogFilterFields,
  FirmDocumentsFilterDialogOption,
  FirmDocumentsFilterDialogOptions,
  FirmDocumentsFilterDialogRoot,
  FirmDocumentsFilterDialogSection,
  FirmDocumentsFilterDialogSectionTitle,
  FirmDocumentsFilterDialogSectionTitleIcon,
  FirmDocumentsFilterDialogSelectTrigger,
  FirmDocumentsFilterDialogSortControl,
  FirmDocumentsFilterDialogSortDirectionButton,
  FirmDocumentsFilterDialogSortOption,
  FirmDocumentsFilterDialogSortOptionButton,
  FirmDocumentsFilterDialogSortOptionLabel,
  FirmDocumentsFilterDialogSortOptionMutedText,
  FirmDocumentsFilterDialogSortOptions,
} from './FirmDocumentsFilterDialog.styles';
import type {
  FirmDocumentsFilterDialogProps,
  FirmDocumentsFilterValue,
} from './FirmDocumentsFilterDialog.types';

type FirmDocumentFilterOptions = NonNullable<FirmDocumentsFilterDialogProps['filterOptions']>;

const EMPTY_FILTER_OPTIONS: FirmDocumentFilterOptions = {
  file_types: [],
  reference_types: [],
  sources: [],
  uploaded_by: [],
};

type DocumentSortCategory = 'filename' | 'uploaded_date';

interface SourceSelectOption {
  label: string;
  source: string | null;
  uploadedBy: string | null;
  value: string;
}

const USER_SOURCE_VALUE = 'user';
const UPLOADED_BY_OPTION_PREFIX = 'uploaded-by';

const DEFAULT_DOCUMENT_ORDERINGS: Record<DocumentSortCategory, string> = {
  filename: 'filename',
  uploaded_date: '-uploaded_date',
};

const REVERSED_DOCUMENT_ORDERINGS: Record<DocumentSortCategory, string> = {
  filename: '-filename',
  uploaded_date: 'uploaded_date',
};

const getDocumentSortCategory = (ordering: string | null): DocumentSortCategory | null => {
  const normalizedOrdering = ordering?.replace('-', '');

  if (normalizedOrdering === 'filename' || normalizedOrdering === 'uploaded_date') {
    return normalizedOrdering;
  }

  return null;
};

const getDocumentSortDirectionLabel = (
  category: DocumentSortCategory,
  ordering: string | null
) => {
  const activeOrdering = ordering ?? DEFAULT_DOCUMENT_ORDERINGS[category];

  if (category === 'filename') {
    return activeOrdering === '-filename' ? 'Z-A' : 'A-Z';
  }

  return activeOrdering === 'uploaded_date' ? 'Oldest First' : 'Newest First';
};

const getNextDocumentOrdering = (category: DocumentSortCategory, currentOrdering: string | null) => (
  currentOrdering === DEFAULT_DOCUMENT_ORDERINGS[category]
    ? REVERSED_DOCUMENT_ORDERINGS[category]
    : DEFAULT_DOCUMENT_ORDERINGS[category]
);

const getNextDocumentSortDirectionLabel = (
  category: DocumentSortCategory,
  currentOrdering: string | null
) => getDocumentSortDirectionLabel(category, getNextDocumentOrdering(category, currentOrdering));

const getUploadedBySourceOptionValue = (uploadedBy: string) => `${UPLOADED_BY_OPTION_PREFIX}:${uploadedBy}`;

const getNormalizedFilterOptions = (
  options?: FirmDocumentsFilterDialogProps['filterOptions']
): FirmDocumentFilterOptions => ({
  file_types: options?.file_types ?? EMPTY_FILTER_OPTIONS.file_types,
  reference_types: options?.reference_types ?? EMPTY_FILTER_OPTIONS.reference_types,
  sources: options?.sources ?? EMPTY_FILTER_OPTIONS.sources,
  uploaded_by: options?.uploaded_by ?? EMPTY_FILTER_OPTIONS.uploaded_by,
});

const getSourceSelectOptions = (options: FirmDocumentFilterOptions): SourceSelectOption[] => [
  ...options.sources
    .filter(source => source.value !== USER_SOURCE_VALUE || options.uploaded_by.length === 0)
    .map(source => ({
      label: source.label,
      source: source.value,
      uploadedBy: null,
      value: source.value,
    })),
  ...options.uploaded_by.map(uploadedBy => ({
    label: uploadedBy.label,
    source: null,
    uploadedBy: uploadedBy.value,
    value: getUploadedBySourceOptionValue(uploadedBy.value),
  })),
];

const getSelectedDocumentOrdering = (optionOrdering: string, currentOrdering: string | null) => {
  const optionCategory = getDocumentSortCategory(optionOrdering);

  if (!optionCategory) {
    return optionOrdering;
  }

  if (getDocumentSortCategory(currentOrdering) === optionCategory) {
    return null;
  }

  return DEFAULT_DOCUMENT_ORDERINGS[optionCategory];
};

const FirmDocumentsFilterDialog = ({
  filterOptions,
  isLoading = false,
  onApply,
  onClose,
  open,
  value,
}: FirmDocumentsFilterDialogProps) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [draftValue, setDraftValue] = useState<FirmDocumentsFilterValue>(value);
  const [sourceAnchorEl, setSourceAnchorEl] = useState<HTMLButtonElement | null>(null);
  const options = useMemo(() => getNormalizedFilterOptions(filterOptions), [filterOptions]);
  const sourceOptions = useMemo(() => getSourceSelectOptions(options), [options]);
  const selectedSource = useMemo(
    () => sourceOptions.find(source =>
      source.source === draftValue.source && source.uploadedBy === draftValue.uploadedBy
    ),
    [draftValue.source, draftValue.uploadedBy, sourceOptions]
  );
  const {
    filteredOptions: filteredSourceOptions,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
  } = useSearchableLimitedOptions({
    getSearchText: source => source.label,
    options: sourceOptions,
  });
  const isSourceMenuOpen = Boolean(sourceAnchorEl);
  const sourceMenuWidth = sourceAnchorEl?.offsetWidth ?? 240;

  const handleSourceMenuClose = useCallback(() => {
    setSourceAnchorEl(null);
    resetSearchableOptions();
  }, [resetSearchableOptions]);

  const handleApply = () => {
    onApply(draftValue);
  };

  const handleReset = () => {
    setDraftValue({
      fileType: null,
      ordering: null,
      referenceObjectType: null,
      source: null,
      uploadedBy: null,
    });
  };

  // Keep draft filters isolated while the dialog is open, then close nested menus on dismiss.
  useEffect(() => {
    if (open) {
      setDraftValue(value);
      return;
    }

    handleSourceMenuClose();
  }, [handleSourceMenuClose, open, value]);

  return (
    <FirmDocumentsFilterDialogRoot
      data-testid={FIRM_DOCUMENTS_TEST_IDS.filterDialog}
      maxWidth="xs"
      onClose={onClose}
      open={open}>
      <DialogHeader>
        <DialogTitle>{FIRM_DOCUMENTS_LABELS.sortAndFilter}</DialogTitle>
        <DialogCloseButton
          aria-label="Close document filters"
          onClick={onClose}
          size="small">
          <DialogCloseIcon />
        </DialogCloseButton>
      </DialogHeader>
      <DialogContent>
        <FirmDocumentsFilterDialogSection>
          <FirmDocumentsFilterDialogSectionTitle>
            <FirmDocumentsFilterDialogSectionTitleIcon>
              <SortIcon />
            </FirmDocumentsFilterDialogSectionTitleIcon>
            {FIRM_DOCUMENTS_LABELS.sortBy}
          </FirmDocumentsFilterDialogSectionTitle>
          <FirmDocumentsFilterDialogSortOptions role="group" aria-label="Sort documents">
            {FIRM_DOCUMENTS_DOCUMENT_ORDERINGS.map(option => {
              const optionCategory = getDocumentSortCategory(option.value);
              const isOptionSelected = Boolean(
                optionCategory && getDocumentSortCategory(draftValue.ordering) === optionCategory
              );
              const directionLabel = optionCategory
                ? getDocumentSortDirectionLabel(optionCategory, isOptionSelected ? draftValue.ordering : option.value)
                : null;
              const nextDirectionLabel = optionCategory
                ? getNextDocumentSortDirectionLabel(
                  optionCategory,
                  isOptionSelected ? draftValue.ordering : option.value
                )
                : null;

              return (
                <FirmDocumentsFilterDialogSortOption
                  key={option.value}
                  isSelected={isOptionSelected}>
                  <FirmDocumentsFilterDialogSortOptionButton
                    aria-pressed={isOptionSelected}
                    data-testid={FIRM_DOCUMENTS_TEST_IDS.filterSortOption(option.value)}
                    isSelected={isOptionSelected}
                    onClick={() => setDraftValue(currentValue => ({
                      ...currentValue,
                      ordering: getSelectedDocumentOrdering(option.value, currentValue.ordering),
                    }))}
                    type="button">
                    <FirmDocumentsFilterDialogSortControl
                      aria-hidden="true"
                      isSelected={isOptionSelected}
                    />
                    <FirmDocumentsFilterDialogSortOptionLabel>
                      {option.label}
                      {directionLabel ? (
                        <>
                          {' '}
                          <FirmDocumentsFilterDialogSortOptionMutedText>
                            (
                            {directionLabel}
                            )
                          </FirmDocumentsFilterDialogSortOptionMutedText>
                        </>
                      ) : null}
                    </FirmDocumentsFilterDialogSortOptionLabel>
                  </FirmDocumentsFilterDialogSortOptionButton>
                  {isOptionSelected && optionCategory && nextDirectionLabel ? (
                    <Tooltip title={`Switch to ${nextDirectionLabel}`} placement="bottom">
                      <FirmDocumentsFilterDialogSortDirectionButton
                        aria-label={`Switch ${option.label} sort to ${nextDirectionLabel}`}
                        onClick={() => setDraftValue(currentValue => ({
                          ...currentValue,
                          ordering: getNextDocumentOrdering(optionCategory, currentValue.ordering),
                        }))}
                        type="button">
                        {optionCategory === 'filename' ? (
                          <SortByAlphaIcon />
                        ) : (
                          <SwapVertIcon />
                        )}
                      </FirmDocumentsFilterDialogSortDirectionButton>
                    </Tooltip>
                  ) : null}
                </FirmDocumentsFilterDialogSortOption>
              );
            })}
          </FirmDocumentsFilterDialogSortOptions>
        </FirmDocumentsFilterDialogSection>
        <FirmDocumentsFilterDialogSection>
          <FirmDocumentsFilterDialogSectionTitle>
            <FirmDocumentsFilterDialogSectionTitleIcon>
              <FilterListIcon />
            </FirmDocumentsFilterDialogSectionTitleIcon>
            {FIRM_DOCUMENTS_LABELS.filterBy}
          </FirmDocumentsFilterDialogSectionTitle>
          <FirmDocumentsFilterDialogFilterFields>
            <FirmDocumentsFilterDialogField>
              <FirmDocumentsFilterDialogFieldLabel>{FIRM_DOCUMENTS_LABELS.source}</FirmDocumentsFilterDialogFieldLabel>
              <FirmDocumentsFilterDialogSelectTrigger
                aria-expanded={isSourceMenuOpen || undefined}
                aria-haspopup="listbox"
                data-testid={FIRM_DOCUMENTS_TEST_IDS.filterSourceSelect}
                disabled={isLoading || sourceOptions.length === 0}
                hasValue={Boolean(selectedSource)}
                onClick={event => setSourceAnchorEl(event.currentTarget)}>
                <DialogSelectTriggerContent
                  isLoading={isLoading}
                  placeholder={FIRM_DOCUMENTS_LABELS.sourcePlaceholder}
                  value={selectedSource?.label}
                />
              </FirmDocumentsFilterDialogSelectTrigger>
              <PrimaryNavigationMenu
                anchorEl={sourceAnchorEl}
                data-testid={FIRM_DOCUMENTS_TEST_IDS.filterSourceMenu}
                disableAutoFocusItem
                menuMaxWidth={sourceMenuWidth}
                menuMinWidth={sourceMenuWidth}
                onClose={handleSourceMenuClose}
                open={isSourceMenuOpen}
                scrollHintItemLabelPlural="sources"
                scrollHintItemLabelSingular="source"
                TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
                MenuListProps={{ disableListWrap: true }}>
                <PrimaryNavigationMenuSearch
                  clearAriaLabel="Clear source search"
                  fullWidth
                  inputRef={searchInputRef}
                  onChange={event => setSearchValue(event.target.value)}
                  onClear={() => setSearchValue('')}
                  placeholder="Find Source"
                  value={searchValue}
                  inputProps={{
                    'data-testid': FIRM_DOCUMENTS_TEST_IDS.filterSourceSearchInput,
                  }}
                />
                {filteredSourceOptions.map(source => (
                  <PrimaryNavigationMenuItem
                    key={source.value}
                    data-testid={FIRM_DOCUMENTS_TEST_IDS.filterSourceOption(source.value)}
                    isActive={source.value === selectedSource?.value}
                    onClick={() => {
                      setDraftValue(currentValue => ({
                        ...currentValue,
                        source: source.source,
                        uploadedBy: source.uploadedBy,
                      }));
                      handleSourceMenuClose();
                    }}>
                    {source.label}
                  </PrimaryNavigationMenuItem>
                ))}
                {filteredSourceOptions.length === 0 ? (
                  <PrimaryNavigationMenuItem disabled tone="muted">
                    No sources found
                  </PrimaryNavigationMenuItem>
                ) : null}
              </PrimaryNavigationMenu>
            </FirmDocumentsFilterDialogField>
            <FirmDocumentsFilterDialogField>
              <FirmDocumentsFilterDialogFieldLabel>{FIRM_DOCUMENTS_LABELS.referenceType}</FirmDocumentsFilterDialogFieldLabel>
              <FirmDocumentsFilterDialogOptions>
                {options.reference_types.map(option => (
                  <FirmDocumentsFilterDialogOption
                    key={option.value}
                    aria-pressed={draftValue.referenceObjectType === option.value}
                    data-testid={FIRM_DOCUMENTS_TEST_IDS.filterReferenceTypeOption(option.value)}
                    isSelected={draftValue.referenceObjectType === option.value}
                    onClick={() => setDraftValue(currentValue => ({
                      ...currentValue,
                      referenceObjectType: currentValue.referenceObjectType === option.value ? null : option.value,
                    }))}
                    type="button">
                    {option.label}
                  </FirmDocumentsFilterDialogOption>
                ))}
              </FirmDocumentsFilterDialogOptions>
            </FirmDocumentsFilterDialogField>
            <FirmDocumentsFilterDialogField>
              <FirmDocumentsFilterDialogFieldLabel>{FIRM_DOCUMENTS_LABELS.fileType}</FirmDocumentsFilterDialogFieldLabel>
              <FirmDocumentsFilterDialogOptions>
                {options.file_types.map(option => {
                  const fileTypeLabel = option.value.toUpperCase();

                  return (
                    <Tooltip key={option.value} title={option.label} placement="bottom">
                      <FirmDocumentsFilterDialogOption
                        aria-pressed={draftValue.fileType === option.value}
                        data-testid={FIRM_DOCUMENTS_TEST_IDS.filterFileTypeOption(option.value)}
                        isSelected={draftValue.fileType === option.value}
                        onClick={() => setDraftValue(currentValue => ({
                          ...currentValue,
                          fileType: currentValue.fileType === option.value ? null : option.value,
                        }))}
                        type="button">
                        <FirmDocumentsFilterDialogFileTypeOptionContent>
                          <FirmDocumentsFilterDialogFileTypeOptionIcon aria-hidden="true">
                            <FileTypeIcon fileType={option.value} />
                          </FirmDocumentsFilterDialogFileTypeOptionIcon>
                          {fileTypeLabel}
                        </FirmDocumentsFilterDialogFileTypeOptionContent>
                      </FirmDocumentsFilterDialogOption>
                    </Tooltip>
                  );
                })}
              </FirmDocumentsFilterDialogOptions>
            </FirmDocumentsFilterDialogField>
          </FirmDocumentsFilterDialogFilterFields>
        </FirmDocumentsFilterDialogSection>
      </DialogContent>
      <DialogActionFooter
        actionButtonTestId={FIRM_DOCUMENTS_TEST_IDS.filterApplyButton}
        actionLabel="Apply Filters"
        cancelButtonTestId={FIRM_DOCUMENTS_TEST_IDS.filterResetButton}
        cancelLabel="Reset Filters"
        isActionDisabled={isLoading}
        onAction={handleApply}
        onCancel={handleReset}
      />
    </FirmDocumentsFilterDialogRoot>
  );
};

export default FirmDocumentsFilterDialog;
